import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { InfiniteData } from '@tanstack/react-query'
import { clearAllInAppNotifications } from '../api'
import type { InAppNotificationListResponse } from '../api'
import { notificationQueryKeys } from './notificationQueryKeys'
import { refreshNotificationsPulse } from './refreshNotificationsPulse'
import type { PulseSnapshot } from '../utils/pulseUtils'

export const useClearAllNotifications = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => clearAllInAppNotifications(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: notificationQueryKeys.list() })

      const previousList = queryClient.getQueriesData<
        InfiniteData<InAppNotificationListResponse>
      >({ queryKey: notificationQueryKeys.list() })

      queryClient.setQueriesData<InfiniteData<InAppNotificationListResponse>>(
        { queryKey: notificationQueryKeys.list() },
        (current) => {
          if (!current) {
            return current
          }
          return {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              data: [],
              nextCursor: null,
            })),
          }
        }
      )

      const previousPulse = queryClient.getQueryData<PulseSnapshot>(
        notificationQueryKeys.pulse()
      )

      queryClient.setQueryData<PulseSnapshot>(notificationQueryKeys.pulse(), {
        unreadCount: 0,
        headId: null,
      })

      return { previousList, previousPulse }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousList) {
        for (const [queryKey, data] of context.previousList) {
          queryClient.setQueryData(queryKey, data)
        }
      }
      if (context?.previousPulse) {
        queryClient.setQueryData(
          notificationQueryKeys.pulse(),
          context.previousPulse
        )
      }
    },
    onSuccess: async () => {
      await refreshNotificationsPulse(queryClient)
      await queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.list(),
      })
    },
  })
}
